import { types } from "../Types/agent";

export interface AgentState {
  agent: any;
  agentLoading: boolean;
  agentError: string | null;
  // Bumped when the server copy of the agent should replace the edit form
  // (initial load and successful save). Example add/remove does not bump it,
  // so unsaved form edits survive.
  agentSyncId: number;
  savingAgent: boolean;
  savingExamples: boolean;
  testResult: any;
  testing: boolean;

  messages: any[];
  messagesLoading: boolean;
  messagesError: string | null;
  stats: any;
  // In-flight draft actions keyed by draft id (or inbound message id for
  // "generate"). Value is the action name, e.g. "send".
  draftPending: Record<string, string>;

  watch: any;
  watchLoading: boolean;

  avatars: any[];
  avatarsLoading: boolean;
  avatarsError: string | null;
  stockFaces: any[];
  stockFacesLoading: boolean;
  stockFacesError: string | null;
  creatingAvatar: boolean;
  avatarPending: Record<string, string>;
}

export const initialState: AgentState = {
  agent: null,
  agentLoading: false,
  agentError: null,
  agentSyncId: 0,
  savingAgent: false,
  savingExamples: false,
  testResult: null,
  testing: false,

  messages: [],
  messagesLoading: false,
  messagesError: null,
  stats: null,
  draftPending: {},

  watch: null,
  watchLoading: false,

  avatars: [],
  avatarsLoading: false,
  avatarsError: null,
  stockFaces: [],
  stockFacesLoading: false,
  stockFacesError: null,
  creatingAvatar: false,
  avatarPending: {},
};

const withKey = (map: Record<string, string>, key: string, value: string) => ({ ...map, [key]: value });
const withoutKey = (map: Record<string, string>, key: string) => {
  if (!key || !(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
};

// Put a serialized draft ({ draft } response) onto the message that owns it.
export const applyDraft = (state: AgentState, draft: any, extra: object = {}): AgentState => {
  if (!draft || !draft.id) return state;
  return {
    ...state,
    messages: state.messages.map((message: any) =>
      message.id === draft.inboundMessageId || (message.draft && message.draft.id === draft.id)
        ? { ...message, draft, draftError: null, ...extra }
        : message
    ),
  };
};

const replaceAvatar = (avatars: any[], avatar: any, clearOtherDefaults: boolean) =>
  avatars.map((entry: any) => {
    if (entry.id === avatar.id) return avatar;
    if (clearOtherDefaults && entry.isDefault) return { ...entry, isDefault: false };
    return entry;
  });

const DRAFT_ACTIONS: Record<string, string> = {
  [types.GENERATE_DRAFT]: "generate",
  [types.UPDATE_DRAFT_BODY]: "save",
  [types.SEND_DRAFT]: "send",
  [types.CREATE_DRAFT_VIDEO]: "video",
};
const DRAFT_DONE = new Set<string>([
  types.GENERATE_DRAFT_SUCCESS,
  types.UPDATE_DRAFT_BODY_SUCCESS,
  types.DECIDE_DRAFT_SUCCESS,
  types.SEND_DRAFT_SUCCESS,
  types.CREATE_DRAFT_VIDEO_SUCCESS,
]);
const DRAFT_FAILED = new Set<string>([
  types.GENERATE_DRAFT_FAILURE,
  types.UPDATE_DRAFT_BODY_FAILURE,
  types.DECIDE_DRAFT_FAILURE,
  types.SEND_DRAFT_FAILURE,
  types.CREATE_DRAFT_VIDEO_FAILURE,
]);

const agentReducer = (state: AgentState = initialState, action: any): AgentState => {
  const payload = action.payload || {};

  // Draft actions: request marks the key in flight, success applies { draft },
  // failure clears the key (generation failure also records draftError).
  if (DRAFT_ACTIONS[action.type]) {
    const key = action.type === types.GENERATE_DRAFT ? payload.inboundMessageId : payload.id;
    return { ...state, draftPending: withKey(state.draftPending, key, DRAFT_ACTIONS[action.type]) };
  }
  if (action.type === types.DECIDE_DRAFT) {
    return { ...state, draftPending: withKey(state.draftPending, payload.id, payload.action) };
  }
  if (DRAFT_DONE.has(action.type)) {
    const next = applyDraft(
      state,
      payload.draft,
      action.type === types.GENERATE_DRAFT_SUCCESS ? { draftPending: false } : {}
    );
    return { ...next, draftPending: withoutKey(next.draftPending, payload.key) };
  }
  if (action.type === types.GENERATE_DRAFT_QUEUED) {
    return {
      ...state,
      messages: state.messages.map((message: any) =>
        message.id === payload.key ? { ...message, draftPending: true, draftError: null } : message
      ),
      draftPending: withoutKey(state.draftPending, payload.key),
    };
  }
  if (DRAFT_FAILED.has(action.type)) {
    let messages = state.messages;
    if (action.type === types.GENERATE_DRAFT_FAILURE) {
      messages = messages.map((message: any) =>
        message.id === payload.key ? { ...message, draftError: payload.error } : message
      );
    }
    return { ...state, messages, draftPending: withoutKey(state.draftPending, payload.key) };
  }

  switch (action.type) {
    case types.GET_AGENT:
      return { ...state, agentLoading: true, agentError: null };
    case types.GET_AGENT_SUCCESS:
      return {
        ...state,
        agentLoading: false,
        agent: action.payload,
        agentSyncId: state.agentSyncId + 1,
      };
    case types.GET_AGENT_FAILURE:
      return { ...state, agentLoading: false, agentError: action.payload };

    case types.UPDATE_AGENT:
      return { ...state, savingAgent: true };
    case types.UPDATE_AGENT_SUCCESS:
      return {
        ...state,
        savingAgent: false,
        agent: action.payload,
        agentSyncId: state.agentSyncId + 1,
      };
    case types.UPDATE_AGENT_FAILURE:
      return { ...state, savingAgent: false };

    case types.ADD_AGENT_EXAMPLE:
    case types.REMOVE_AGENT_EXAMPLE:
      return { ...state, savingExamples: true };
    case types.ADD_AGENT_EXAMPLE_SUCCESS:
    case types.REMOVE_AGENT_EXAMPLE_SUCCESS:
      return { ...state, savingExamples: false, agent: action.payload };
    case types.ADD_AGENT_EXAMPLE_FAILURE:
    case types.REMOVE_AGENT_EXAMPLE_FAILURE:
      return { ...state, savingExamples: false };

    case types.TEST_AGENT:
      return { ...state, testing: true, testResult: null };
    case types.TEST_AGENT_SUCCESS:
      return { ...state, testing: false, testResult: action.payload };
    case types.TEST_AGENT_FAILURE:
      return { ...state, testing: false, testResult: { error: action.payload } };
    case types.CLEAR_AGENT_TEST:
      return { ...state, testResult: null };

    case types.GET_INBOX_MESSAGES:
      return { ...state, messagesLoading: true, messagesError: null };
    case types.GET_INBOX_MESSAGES_SUCCESS:
      return { ...state, messagesLoading: false, messages: action.payload || [] };
    case types.GET_INBOX_MESSAGES_FAILURE:
      return { ...state, messagesLoading: false, messagesError: action.payload };

    case types.GET_DRAFT_STATS_SUCCESS:
      return { ...state, stats: action.payload };

    case types.FETCH_DRAFT_SUCCESS:
      return applyDraft(state, payload.draft);

    case types.GET_WATCH_SUCCESS:
      return { ...state, watch: action.payload };
    case types.START_WATCH:
    case types.STOP_WATCH:
      return { ...state, watchLoading: true };
    case types.START_WATCH_SUCCESS:
    case types.START_WATCH_FAILURE:
    case types.STOP_WATCH_FAILURE:
      return { ...state, watchLoading: false };
    case types.STOP_WATCH_SUCCESS:
      return { ...state, watchLoading: false, watch: { ...(state.watch || {}), active: false } };

    case types.GET_AVATARS:
      return { ...state, avatarsLoading: true, avatarsError: null };
    case types.GET_AVATARS_SUCCESS:
      return { ...state, avatarsLoading: false, avatars: action.payload || [] };
    case types.GET_AVATARS_FAILURE:
      return { ...state, avatarsLoading: false, avatarsError: action.payload };

    case types.GET_STOCK_FACES:
      return { ...state, stockFacesLoading: true, stockFacesError: null };
    case types.GET_STOCK_FACES_SUCCESS:
      return { ...state, stockFacesLoading: false, stockFaces: action.payload || [] };
    case types.GET_STOCK_FACES_FAILURE:
      return { ...state, stockFacesLoading: false, stockFacesError: action.payload };

    case types.CREATE_AVATAR:
      return { ...state, creatingAvatar: true };
    case types.CREATE_AVATAR_SUCCESS: {
      const avatar = action.payload;
      const others = avatar.isDefault
        ? state.avatars.map((entry: any) => ({ ...entry, isDefault: false }))
        : state.avatars;
      return { ...state, creatingAvatar: false, avatars: [avatar, ...others] };
    }
    case types.CREATE_AVATAR_FAILURE:
      return { ...state, creatingAvatar: false };

    case types.SYNC_AVATAR_SUCCESS:
      return { ...state, avatars: replaceAvatar(state.avatars, action.payload, false) };

    case types.SET_DEFAULT_AVATAR:
      return { ...state, avatarPending: withKey(state.avatarPending, payload.id, "default") };
    case types.SET_DEFAULT_AVATAR_SUCCESS:
      return {
        ...state,
        avatars: replaceAvatar(state.avatars, action.payload, true),
        avatarPending: withoutKey(state.avatarPending, action.payload.id),
      };
    case types.DELETE_AVATAR:
      return { ...state, avatarPending: withKey(state.avatarPending, payload.id, "delete") };
    case types.DELETE_AVATAR_SUCCESS:
      return {
        ...state,
        avatars: state.avatars.filter((entry: any) => entry.id !== action.payload),
        avatarPending: withoutKey(state.avatarPending, action.payload),
      };
    case types.SET_DEFAULT_AVATAR_FAILURE:
    case types.DELETE_AVATAR_FAILURE:
      return { ...state, avatarPending: withoutKey(state.avatarPending, payload.id) };

    default:
      return state;
  }
};

export default agentReducer;
